import { ReactComponent as LogoIdA } from "@/components/layout/logo-ida.svg";
import strakeBianco from "@/components/layout/strake-bianco.svg";
import strakeNero from "@/components/layout/strake-nero.svg";
import { cn } from "@/lib/utils";

/**
 * La cornice di ogni pagina che si apre senza il menu: accesso, codice di verifica, invito, attivazione
 * (blocco 4 del pacchetto del 29-10, tavole B4-accesso / B4-codice / B4-benvenuto).
 * Dal computer (1024) un pannello blu da 560 px a sinistra e il modulo a destra; sotto, la banda blu col
 * logo in testa e il modulo bianco. Il contenuto è solo `children`: titolo, campi e pulsanti li mette la pagina.
 */
export function PoweredBy({ chiaro, className }) {
  return (
    <span className={cn("flex items-center gap-2 text-caption font-normal", chiaro ? "text-white/75" : "text-ink-3", className)}>
      Powered by
      <img src={chiaro ? strakeBianco : strakeNero} alt="Strake" className="h-3.5 w-auto" />
    </span>
  );
}

export default function AuthShell({ children, colonna = "max-w-[420px]", testId }) {
  return (
    <div data-testid={testId} className="flex min-h-screen flex-col bg-surface lg:flex-row lg:bg-page">
      <aside className="on-blue hidden shrink-0 flex-col justify-between bg-nav p-12 text-white lg:flex lg:basis-[min(560px,40%)] xl:px-16 xl:pb-10 xl:pt-14">
        <LogoIdA className="w-60 max-w-full" aria-label="Interni D'Autore Arredamenti" />
        <div className="flex flex-col gap-3">
          <p className="font-display text-[34px] font-semibold leading-[42px]">Il gestionale di Selva di Altino e Vasto Marina</p>
          <p className="max-w-[400px] text-base leading-6 text-white/85">Clienti, visite, preventivi, contratti e assistenza, in un posto solo.</p>
        </div>
        <PoweredBy chiaro />
      </aside>

      <header className="on-blue bg-nav px-6 pb-5 pt-[calc(env(safe-area-inset-top,0px)+20px)] text-white lg:hidden">
        <LogoIdA className="w-[168px]" aria-label="Interni D'Autore Arredamenti" />
      </header>

      <main className="flex flex-1 flex-col items-center px-6 pb-4 pt-6 sm:pt-10 lg:justify-center lg:p-10">
        <div className={cn("w-full animate-ida-rise", colonna)}>{children}</div>
      </main>

      <footer className="flex justify-center pb-6 lg:hidden">
        <PoweredBy />
      </footer>
    </div>
  );
}

/** L'avviso dentro il modulo: errore (rosso) o esito (verde). Una riga che dice cosa è successo e cosa fare. */
export function Avviso({ tono = "danger", className, children, ...props }) {
  return (
    <p
      role={tono === "danger" ? "alert" : "status"}
      className={cn(
        "animate-ida-rise rounded-md px-3.5 py-3 font-semibold",
        tono === "danger" ? "bg-danger-bg text-danger-ink" : "bg-success-bg text-success-ink",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

/** Titolo e frase sotto, uguali in tutte le pagine della cornice. */
export function TestataModulo({ titolo, children, icona: Icona }) {
  return (
    <div className="flex flex-col gap-[18px]">
      {Icona ? (
        <span aria-hidden="true" className="inline-flex size-12 items-center justify-center rounded-lg bg-brand-subtle text-brand">
          <Icona className="size-6" strokeWidth={1.75} />
        </span>
      ) : null}
      <div>
        <h1 className="font-display text-h1-mobile text-ink-strong sm:text-h1">{titolo}</h1>
        {children ? <p className="mt-2 text-ink-2">{children}</p> : null}
      </div>
    </div>
  );
}
